/**
 * injectSidebarPages - Inyecta configuración de páginas visibles en el sidebar
 *
 * Este módulo permite controlar qué páginas se muestran en el sidebar de la aplicación
 * mediante la configuración global HELLOMATIK_CONFIG. El elemento "new-chat" siempre
 * se muestra independientemente de la configuración.
 *
 * @module injectSidebarPages
 */

/**
 * Interfaz para la configuración de páginas del sidebar
 */
export interface SidebarPagesConfig {
  /**
   * Array de IDs de páginas a mostrar en el sidebar
   * - "new-chat" siempre se muestra ( no es necesario incluirlo)
   * - "spaces" para mostrar la página de espacios
   * - "dashboard" para mostrar el dashboard personalizado
   * - "contents" para mostrar la gestión de contenidos
   * - "workflows" para mostrar la gestión de workflows
   * - "training" para mostrar la página de formación
   * - "integrations" para mostrar la gestión de integraciones
   * - "activity" para mostrar el registro de actividad
   */
  pages?: string[]
}

/**
 * Inyecta la configuración de páginas visibles en el sidebar
 *
 * Lee la configuración de sidebar-pages desde HELLOMATIK_CONFIG y la guarda
 * en una variable global para que el sidebar pueda acceder a ella.
 *
 * @example
 * // En la configuración del core:
 * {
 *   "config_id": "sidebar-pages",
 *   "config_value": {
 *     "pages": ["spaces", "dashboard", "contents", "workflows", "training", "integrations", "activity"]
 *   }
 * }
 *
 * // Para mostrar solo algunas páginas:
 * {
 *   "config_id": "sidebar-pages", 
 *   "config_value": {
 *     "pages": ["spaces", "contents"]
 *   }
 * }
 *
 * // En el loader, después de cargar la configuración:
 * injectSidebarPages();
 *
 * @returns {void}
 */
export function injectSidebarPages(): void {
  try {
    // Obtener la configuración desde la variable global
    const config = ( window as any ).HELLOMATIK_CONFIG;

    if( !config )
      return;

    // Obtener la configuración de páginas del sidebar
    const sidebarPages = config['sidebar-pages'] as SidebarPagesConfig;

    if( !sidebarPages || typeof sidebarPages !== 'object' )
      return;

    // Verificar que pages sea un array
    if( !Array.isArray( sidebarPages.pages ) )
      return;

    // Guardar la configuración en una variable global específica para el sidebar
    ( window as any ).HELLOMATIK_SIDEBAR_PAGES = sidebarPages.pages;

  } catch( error ) {
    if( import.meta.env.DEV ) console.error( '[Sidebar Pages] Failed to inject sidebar pages configuration:', error );
  }
}

/**
 * Resetea la configuración de páginas del sidebar
 * Útil para pruebas o cuando se quiere volver a la configuración por defecto
 *
 * @returns {void}
 */
export function resetSidebarPages(): void {
  if( ( window as any ).HELLOMATIK_SIDEBAR_PAGES )
    delete ( window as any ).HELLOMATIK_SIDEBAR_PAGES;
}
